import * as Yup from "utils/yup"
import i18next from "i18next"
import {
  findOverlappingBoundary,
  hasRingSelfIntersection,
  isPolygonRingClosed,
} from "common/geoBoundaryUtils"
import {
  getAlfaNumericChar,
  getArabicDescMessage,
  getArabicLinkMessage,
  getDialCodeFormatMessage,
  getCountryCodeFormatMessage,
  getDisplayNumMessage,
  getExpiredDateMsg,
  getFieldOnlyLetters,
  getRoleNameArabicMustHaveLetterMessage,
  getRoleNameInvalidCharsMessage,
  getRoleNameMinLengthMessage,
  getRoleNameMaxLengthMessage,
  getRoleNameMustHaveLetterMessage,
  getRoleDescriptionUnsafeContentMessage,
  getNameNoParenthesesMessage,
  getBrandNameMustHaveLetterMessage,
  getBrandNameInvalidCharsMessage,
  getBrandNameMinLengthMessage,
  getBrandNameMaxLengthMessage,
  getBrandNameSpecialCharsOnlyMessage,
  getBrandNameUnsafeContentMessage,
  getBrandNameInvalidCatalogMessage,
  getBrandTextUnsafeContentMessage,
  getBrandAppBrandIdInvalidMessage,
  getBrandAppBrandIdMaxLengthMessage,
  getBrandUrlInvalidMessage,
  getInvalidImageUrlMessage,
  getInvalidLatitudeMessage,
  getInvalidLongitudeMessage,
  getBrandLocationInvalidCharsMessage,
  getBrandPostalCodeInvalidMessage,
  getDescriptionMaxLengthMessage,
  getFieldRequiredMessage,
  getMinimumCountMessage,
  getGreaterOrEqual0,
  getInvalidFormat,
  getMaxExact1,
  getMaxMustGreaterMin,
  getMin6PasswordMessage,
  getPasswordStrengthMessage,
  getMaxPasswordMessage,
  getMinExact1,
  getMinimumNegMessage,
  getMinOneCustomer,
  getMinOneItemGroup,
  getMinOneLocation,
  getMinOneSetting,
  getNoSpaceMessage,
  getOnlyNumAndChar,
  getBulkCouponPrefixMaxLengthMessage,
  getCouponNameMaxLengthMessage,
  getCouponCodeLengthMessage,
  getInvalidCsvFileMessage,
  getOnlyNumMessage,
  getSafeNumberLimitMessage,
  getPasswordMatchMessage,
  getQtyNum,
  getQtyPositive,
  getValidArabicMessage,
  getEnglishTextOnlyMessage,
  getArabicTextOnlyMessage,
  getEnglishNameMustHaveLetterMessage,
  getArabicNameMustHaveLetterMessage,
  getCountryNameEnglishInvalidCharsMessage,
  getCountryNameArabicInvalidCharsMessage,
  getCountryNameHyphenMessage,
  getQtyInteger,
  getWholeNumberMessage,
  getValidNumMessage,
  getValidPhoneMessage,
  getValue0To100,
  getValueBt1To1000,
  getValueBt3To10,
  getNotificationTextEnglishLetterMessage,
  getNotificationTextArabicLetterMessage,
  getNotificationTextMaxLengthMessage,
  getNotificationTextUnsafeContentMessage,
} from "./validationMessages"
import {
  itemWidth,
  itemHeight,
  categoryWidth,
  categoryHeight,
  modifierGroupWidth,
  modifierGroupHeight,
  modifierOptionWidth,
  modifierOptionHeight,
  bannerWidth,
  bannerHeight,
  webBannerWidth,
  webBannerHeight,
  branchWidth,
  branchHeight,
} from "components/Common/ImageSize"
import {
  getSyncInvalidImageUrlMessage,
  validateRemoteImageUrl,
} from "utils/imageUrlValidation"
import { validatePersonName } from "utils/personNameValidation"
import { isValidPhoneValue } from "utils/phoneUtils"
import {
  buildEmailYupSchema,
  buildOptionalEmailYupSchema,
  getEmailValidationErrorMessage,
  validateEmailAddress,
} from "utils/emailValidation"
import {
  PASSWORD_MAX_LENGTH,
  isStrongPassword,
} from "utils/passwordValidation"
import { containsUnsafeTextContent } from "utils/unsafeTextContent"
import { NUMERIC_SAFE_MAX } from "utils/numericValidation"

// const arabicNameOrNumberPattern = /^[\u0600-\u06FF0-9\s]+$/
const arabicNameOrNumberPattern =
  /^[\u0600-\u06FF0-9\s\p{P}\p{S}\p{M}\u200c\u200d]+$/u

const arabicLetterPattern = /[\u0621-\u064A]/

/** Max length for menu entity name fields (English and Arabic). */
export const ENTITY_NAME_MAX_LENGTH = 255

/** Max length for description fields (role, plan, brand, FAQ, etc.). */
export const DESCRIPTION_MAX_LENGTH = 2000

const englishNamePattern = /^[a-zA-Z\s\-'().]+$/
const englishNameLetterPattern = /[a-zA-Z]/
const arabicNameLettersOnlyPattern = /^[\u0621-\u064A\s\-'().]+$/
const englishQuestionPattern =
  /^[a-zA-Z0-9\s\-'().,!?;:"“”‘’/&]+$/
const arabicQuestionPattern =
  /^[\u0621-\u064A\u060C\u061B\u061F0-9\s\-'().,!?;:"“”‘’«»/&?]+$/

const countryNameEnglishPattern = /^[a-zA-Z\s-]+$/
const countryNameArabicPattern = /^[\u0621-\u064A\s-]+$/

const isValidCountryNameHyphenUsage = value => {
  if (!value || !value.includes("-")) return true
  const trimmed = value.trim()
  if (trimmed.startsWith("-") || trimmed.endsWith("-")) return false
  if (/--/.test(trimmed)) return false
  return true
}

const countryNameEnglishField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(
      ENTITY_NAME_MAX_LENGTH,
      i18next.t("Name cannot exceed 255 characters")
    )
    .matches(
      countryNameEnglishPattern,
      getCountryNameEnglishInvalidCharsMessage()
    )
    .test(
      "english-name-has-letter",
      getEnglishNameMustHaveLetterMessage(),
      value => englishNameLetterPattern.test(value || "")
    )
    .test("country-name-hyphen", getCountryNameHyphenMessage(), value =>
      isValidCountryNameHyphenUsage(value)
    )

const countryNameArabicField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(
      ENTITY_NAME_MAX_LENGTH,
      i18next.t("Name cannot exceed 255 characters")
    )
    .matches(countryNameArabicPattern, getCountryNameArabicInvalidCharsMessage())
    .test(
      "arabic-name-has-letter",
      getArabicNameMustHaveLetterMessage(),
      value => arabicLetterPattern.test(value || "")
    )
    .test("country-name-hyphen", getCountryNameHyphenMessage(), value =>
      isValidCountryNameHyphenUsage(value)
    )

const englishNameField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .matches(englishNamePattern, getFieldOnlyLetters())
    .test(
      "english-name-has-letter",
      getEnglishNameMustHaveLetterMessage(),
      value => englishNameLetterPattern.test(value || "")
    )

const arabicNameField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .matches(arabicNameLettersOnlyPattern, getValidArabicMessage())
    .test(
      "arabic-name-has-letter",
      getArabicNameMustHaveLetterMessage(),
      value => arabicLetterPattern.test(value || "")
    )

/** Question text: letters plus sentence punctuation used in questions. */
const englishQuestionField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .matches(englishQuestionPattern, getFieldOnlyLetters())
    .test(
      "english-name-has-letter",
      getEnglishNameMustHaveLetterMessage(),
      value => englishNameLetterPattern.test(value || "")
    )

const arabicQuestionField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .matches(arabicQuestionPattern, getValidArabicMessage())
    .test(
      "arabic-name-has-letter",
      getArabicNameMustHaveLetterMessage(),
      value => arabicLetterPattern.test(value || "")
    )

/** English description/text: no Arabic script; allows letters, digits, and punctuation. */
const englishDescriptionField = (maxLength = DESCRIPTION_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .max(maxLength, getDescriptionMaxLengthMessage(maxLength))
    .test(
      "no-unsafe-content",
      getRoleDescriptionUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test(
      "english-description-only",
      getEnglishTextOnlyMessage(),
      value => !value || !/[\u0600-\u06FF]/.test(value)
    )

/** Arabic description/text: Arabic script only (plus digits/punctuation); empty allowed. */
const arabicDescriptionField = (maxLength = DESCRIPTION_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .max(maxLength, getDescriptionMaxLengthMessage(maxLength))
    .test(
      "no-unsafe-content",
      getRoleDescriptionUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test(
      "arabic-description-only",
      getArabicTextOnlyMessage(),
      value => {
        if (!value) return true
        if (!arabicNameOrNumberPattern.test(value)) return false
        return arabicLetterPattern.test(value)
      }
    )

/** Optional English title/label (no Arabic script). */
const englishOptionalTitleField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .test(
      "english-title-only",
      getEnglishTextOnlyMessage(),
      value => !value || !/[\u0600-\u06FF]/.test(value)
    )

/** Optional Arabic title/label. */
const arabicOptionalTitleField = (maxLength = ENTITY_NAME_MAX_LENGTH) =>
  Yup.string()
    .trim()
    .max(maxLength, i18next.t("Name cannot exceed 255 characters"))
    .test(
      "arabic-title-only",
      getArabicTextOnlyMessage(),
      value => {
        if (!value) return true
        if (!arabicNameOrNumberPattern.test(value)) return false
        return arabicLetterPattern.test(value)
      }
    )

/** Whole-number display order (>= 0). Used across menu modules. */
const integerDisplayOrderField = () =>
  Yup.number()
    .typeError(getDisplayNumMessage())
    .integer(getWholeNumberMessage())
    .min(0, getGreaterOrEqual0())
    .required(getFieldRequiredMessage())

/** Non-negative numeric string (optional). Rejects negatives and non-numeric input. */
// const optionalNonNegativeNumberField = () =>
//   Yup.string()
//     .nullable()
//     .test(
//       "optional-non-negative",
//       getGreaterOrEqual0(),
//       value => {
//         if (value === null || value === undefined || value === "") return true
//         const str = String(value).trim()
//         if (!/^\d+(\.\d{1,2})?$/.test(str)) return false
//         return Number(str) >= 0
//       }
//     )

const isNegativeNumberValue = value => {
  if (value === null || value === undefined || value === "") return false
  const str = String(value).trim()
  if (!str) return false
  return /^-/.test(str) || Number(str) < 0
}

const requiredNonNegativeNumberField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage())
    .test("non-negative", getGreaterOrEqual0(), value => Number(value) >= 0)

/** Required amount >= 0. Negatives fail before the format check so the non-negative message is shown. */
const requiredZoneAmountField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .test(
      "non-negative",
      getGreaterOrEqual0(),
      value => !isNegativeNumberValue(value)
    )
    .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage())

/** Required whole number >= 0. A negative value uses the item-form non-negative message. */
const requiredNonNegativeWholeNumberField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .test(
      "non-negative",
      getGreaterOrEqual0(),
      value => !isNegativeNumberValue(value)
    )
    .matches(/^\d+$/, getWholeNumberMessage())

/** Person count per serving: required, non-negative whole number (up to 2 digits). */
const personCountPerServingField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .test(
      "person-count-non-negative",
      getGreaterOrEqual0(),
      value => {
        if (value === null || value === undefined || value === "") return true
        const str = String(value).trim()
        if (/^-/.test(str) || Number(str) < 0) return false
        return true
      }
    )
    .matches(
      /^\d{1,2}$/,
      i18next.t("Please enter a valid number up to {{value}} digits", {
        value: 2,
      })
    )

const ALLOWED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"]

const imageFileField = ({ required = false } = {}) => {
  let schema = Yup.mixed().nullable()
  if (required) {
    schema = schema.test(
      "image-file-required",
      getFieldRequiredMessage(),
      value => {
        if (value instanceof File || value instanceof Blob) return true
        return typeof value === "string" && value.trim().length > 0
      }
    )
  }
  return schema.test(
    "image-file-type",
    i18next.t("Please upload a valid image file (JPEG, PNG, or WebP)"),
    value => {
      if (!value || typeof value === "string") return true
      if (!(value instanceof File) && !(value instanceof Blob)) return true
      const type = (value.type || "").toLowerCase()
      if (ALLOWED_IMAGE_MIME_TYPES.includes(type)) return true
      const name = (value.name || "").toLowerCase()
      return /\.(jpe?g|png|webp)$/.test(name)
    }
  )
}

/**
 * Shared Image URL validator for tenant portal forms.
 * - Requires a valid http(s) URL format
 * - Loads the URL in the browser to confirm it is a real image
 * - Optionally enforces exact WxH and/or square aspect (matching upload rules)
 */
const imageUrlField = ({
  required = false,
  exactWidth,
  exactHeight,
  requireSquare = false,
  recommendedWidth,
  recommendedHeight,
} = {}) => {
  let schema = Yup.string().trim()
  if (required) {
    schema = schema.required(getFieldRequiredMessage())
  }
  return schema.test("valid-remote-image", function (value) {
    const trimmed = String(value || "").trim()
    if (!trimmed) return true

    const syncMessage = getSyncInvalidImageUrlMessage(trimmed)
    if (syncMessage) {
      return this.createError({ message: syncMessage })
    }

    const yupThis = this
    return validateRemoteImageUrl(trimmed, {
      exactWidth,
      exactHeight,
      requireSquare,
      recommendedWidth,
      recommendedHeight,
    }).then(result => {
      if (result.valid) return true

      if (result.code === "wrong_dimensions") {
        return yupThis.createError({
          message: i18next.t(
            "Image dimensions must be {{itemWidth}}x{{itemHeight}} pixels.",
            {
              itemWidth: exactWidth ?? result.expectedWidth,
              itemHeight: exactHeight ?? result.expectedHeight,
            }
          ),
        })
      }

      if (result.code === "not_square") {
        const widthHint =
          recommendedWidth ?? exactWidth ?? result.recommendedWidth ?? ""
        const heightHint =
          recommendedHeight ?? exactHeight ?? result.recommendedHeight ?? ""
        return yupThis.createError({
          message: i18next.t(
            "Please upload a square image with recommended dimensions of {{width}} × {{height}} px.",
            { width: widthHint, height: heightHint }
          ),
        })
      }

      return yupThis.createError({ message: getInvalidImageUrlMessage() })
    })
  })
}

const roleNameEnglishPattern = /^[a-zA-Z0-9\s\-_'&.]+$/

/** Max length for role name fields (English and Arabic). */
export const ROLE_NAME_MAX_LENGTH = 100

/** Max length for person first/last name fields. */
export { PERSON_NAME_MAX_LENGTH } from "utils/personNameValidation"

const noParenthesesPattern = /^[^()]*$/

const personNameField = ({ required = true } = {}) =>
  Yup.string()
    .trim()
    .test("person-name", function (value) {
      const message = validatePersonName(value, { required })
      if (!message) return true
      return this.createError({ message })
    })

const phoneNumberField = ({ required = false } = {}) => {
  let schema = Yup.string().nullable()
  if (required) {
    schema = schema.required(getFieldRequiredMessage())
  }
  return schema.test("valid-phone", getValidPhoneMessage(), value => {
    if (!value || !String(value).trim()) {
      return !required
    }
    return isValidPhoneValue(value)
  })
}

const noOnlySpacesOrNumbers = Yup.string()
  .test(
    "no-only-spaces",
    getFieldRequiredMessage(),
    value => (value == null ? false : value.trim().length > 0)
  )
  .test("no-only-numbers", "Field cannot be only numbers", value =>
    isNaN(value)
  )

const createSchemaShape = shape => {
  return Yup.object().shape(shape)
}

const loyaltySafeNumericDigitsTest = function (value) {
  if (value === null || value === undefined || value === "") return true

  const str = String(value).trim()
  const number = Number(str)
  const exceedsSafeLimit =
    str === "Infinity" ||
    str === "-Infinity" ||
    /e/i.test(str) ||
    (Number.isFinite(number) && number > NUMERIC_SAFE_MAX)

  if (exceedsSafeLimit) {
    return this.createError({ message: getSafeNumberLimitMessage() })
  }
  if (!/^[0-9]+$/.test(str)) {
    return this.createError({ message: getOnlyNumMessage() })
  }
  return true
}

const loyaltyRequiredPointsField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .test("safe-numeric-digits", loyaltySafeNumericDigitsTest)

const loyaltyOptionalPointsField = () =>
  Yup.string().nullable().test("safe-numeric-digits", loyaltySafeNumericDigitsTest)

export const LOYALTY_REMARK_MAX_LENGTH = 255

const loyaltyRemarkPattern =
  /^[\u0621-\u064Aa-zA-Z0-9\u060C\u061B\u061F\s.,!?;:'"“”‘’«»()\/&?-]+$/

const loyaltyRemarkField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(
      LOYALTY_REMARK_MAX_LENGTH,
      i18next.t("Text cannot exceed {{max}} characters", {
        max: LOYALTY_REMARK_MAX_LENGTH,
      })
    )
    .test(
      "no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .matches(
      loyaltyRemarkPattern,
      i18next.t(
        "Remark may only contain letters, numbers, spaces, and basic punctuation"
      )
    )

const loyaltyExpiresAtField = () =>
  Yup.mixed()
    .nullable()
    .transform(value => (value === "" || value === undefined ? null : value))
    .test(
      "future-expiry",
      i18next.t("Expiry date and time must be in the future"),
      value => {
        if (value === null || value === undefined || value === "") return true
        const date = value instanceof Date ? value : new Date(value)
        return !Number.isNaN(date.getTime()) && date.getTime() > Date.now()
      }
    )

/** Max length for branch address fields (English and Arabic). */
export const BRANCH_ADDRESS_MAX_LENGTH = 500

const coordinateNumberPattern = /^-?\d+(\.\d{1,9})?$/

/** Normalize map/manual coords — Google Maps returns ~15 decimal places but validation allows 9. */
export const formatCoordinateForField = value => {
  if (value === null || value === undefined || value === "") return ""
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value).trim()
  return Number(num.toFixed(8)).toString()
}

const latitudeField = () =>
  Yup.string()
    .transform((_value, originalValue) => formatCoordinateForField(originalValue))
    .trim()
    .required(getFieldRequiredMessage())
    .matches(coordinateNumberPattern, getValidNumMessage())
    .test("latitude-range", getInvalidLatitudeMessage(), value => {
      const number = Number(value)
      return Number.isFinite(number) && number >= -90 && number <= 90
    })

const longitudeField = () =>
  Yup.string()
    .transform((_value, originalValue) => formatCoordinateForField(originalValue))
    .trim()
    .required(getFieldRequiredMessage())
    .matches(coordinateNumberPattern, getValidNumMessage())
    .test("longitude-range", getInvalidLongitudeMessage(), value => {
      const number = Number(value)
      return Number.isFinite(number) && number >= -180 && number <= 180
    })

const branchAddressField = ({ arabic = false } = {}) => {
  let schema = Yup.string()
    .trim()
    .max(
      BRANCH_ADDRESS_MAX_LENGTH,
      i18next.t("Text cannot exceed {{max}} characters", {
        max: BRANCH_ADDRESS_MAX_LENGTH,
      })
    )
  if (arabic) {
    schema = schema.matches(
      arabicNameOrNumberPattern,
      getValidArabicMessage()
    )
  }
  return schema
}

export const addLocationSchema = mode => {
  let shape = {
    locationNameArabic: arabicNameField(),
    address: branchAddressField(),
    addressAr: branchAddressField({ arabic: true }),
    locationNameEnglish: englishNameField(),
    // country: Yup.string().required(getFieldRequiredMessage()),
    menu: Yup.object().required(getFieldRequiredMessage()),
    brand: Yup.object().required(getFieldRequiredMessage()),
    email: buildOptionalEmailYupSchema(),
    telephone: phoneNumberField({ required: false }),
    latitude: latitudeField(),
    longitude: longitudeField(),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageURL: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          requireSquare: true,
          recommendedWidth: branchWidth,
          recommendedHeight: branchHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
    countryId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),

    regionId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),

    cityId: Yup.object()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        return value && Object.keys(value).length > 0 // Check if the object is not empty
      })
      .required(getFieldRequiredMessage()),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
      branchId: Yup.string()
        .matches(/^\S+$/, getNoSpaceMessage())
        .required(getFieldRequiredMessage()),
    }
  }

  return createSchemaShape(shape)
}

/** Max length for brand name. */
export const BRAND_NAME_MAX_LENGTH = 100
/** Max length for brand city/state/country. */
export const BRAND_LOCATION_MAX_LENGTH = 100
/** Max length for brand postal code. */
export const BRAND_POSTAL_CODE_MAX_LENGTH = 20

const brandNamePattern =
  /^[a-zA-Z0-9\u0621-\u064A\s\-_'&.]+$/
const brandNameLetterPattern = /[\u0621-\u064Aa-zA-Z]/
const appBrandIdPattern = /^[a-zA-Z0-9_-]+$/
const brandLocationPattern =
  /^(?=.*[\u0621-\u064Aa-zA-Z])[\u0621-\u064Aa-zA-Z\s\-']+$/
const brandPostalCodePattern = /^[a-zA-Z0-9\s\-]+$/

const isValidHttpUrl = value => {
  if (!value || !String(value).trim()) {
    return true
  }
  const trimmed = String(value).trim()
  // Require scheme:// literally so values like "http:asfds" are rejected
  if (!/^https?:\/\//i.test(trimmed) || /\s/.test(trimmed)) {
    return false
  }

  let parsed
  try {
    parsed = new URL(trimmed)
  } catch {
    return false
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return false
  }

  const host = parsed.hostname
  if (!host) {
    return false
  }

  if (host.toLowerCase() === "localhost") {
    return true
  }

  // IPv4
  if (
    /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/.test(
      host
    )
  ) {
    return true
  }

  // IPv6 (URL hostname is without brackets)
  if (host.includes(":")) {
    return /^[0-9a-fA-F:]+$/.test(host)
  }

  // Registered domain: label(s).tld (e.g. example.com)
  return /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(
    host
  )
}

const brandUnsafeTextField = (maxLength = 500) =>
  Yup.string()
    .trim()
    .max(maxLength, i18next.t("Text cannot exceed {{max}} characters", { max: maxLength }))
    .test(
      "no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )

const brandUrlField = () =>
  brandUnsafeTextField(500).test(
    "valid-http-url",
    getBrandUrlInvalidMessage(),
    value => isValidHttpUrl(value)
  )

const brandLocationField = () =>
  Yup.string()
    .trim()
    .max(
      BRAND_LOCATION_MAX_LENGTH,
      i18next.t("Text cannot exceed {{max}} characters", {
        max: BRAND_LOCATION_MAX_LENGTH,
      })
    )
    .test(
      "no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test(
      "valid-location-chars",
      getBrandLocationInvalidCharsMessage(),
      value => !value || brandLocationPattern.test(String(value).trim())
    )

const brandPostalCodeField = () =>
  Yup.string()
    .trim()
    .max(
      BRAND_POSTAL_CODE_MAX_LENGTH,
      i18next.t("Text cannot exceed {{max}} characters", {
        max: BRAND_POSTAL_CODE_MAX_LENGTH,
      })
    )
    .test(
      "no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test(
      "valid-postal-code",
      getBrandPostalCodeInvalidMessage(),
      value => !value || brandPostalCodePattern.test(String(value).trim())
    )

const brandNameField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .min(2, getBrandNameMinLengthMessage())
    .max(BRAND_NAME_MAX_LENGTH, getBrandNameMaxLengthMessage(BRAND_NAME_MAX_LENGTH))
    .matches(brandNamePattern, getBrandNameInvalidCharsMessage())
    .matches(noParenthesesPattern, getNameNoParenthesesMessage())
    .test(
      "brand-name-has-letter",
      getBrandNameMustHaveLetterMessage(),
      value => brandNameLetterPattern.test(value || "")
    )
    .test(
      "brand-name-not-special-only",
      getBrandNameSpecialCharsOnlyMessage(),
      value => !value || !/^[\s\p{P}\p{S}]+$/u.test(String(value).trim())
    )
    .test(
      "brand-name-no-unsafe-content",
      getBrandNameUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )

const appBrandIdField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(BRAND_NAME_MAX_LENGTH, getBrandAppBrandIdMaxLengthMessage(BRAND_NAME_MAX_LENGTH))
    .matches(appBrandIdPattern, getBrandAppBrandIdInvalidMessage())
    .test(
      "app-brand-id-no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )

const roleDescriptionField = () =>
  Yup.string()
    .trim()
    .max(
      DESCRIPTION_MAX_LENGTH,
      getDescriptionMaxLengthMessage(DESCRIPTION_MAX_LENGTH)
    )
    .test(
      "no-unsafe-content",
      getRoleDescriptionUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test(
      "english-role-description-only",
      getEnglishTextOnlyMessage(),
      value => !value || !/[\u0600-\u06FF]/.test(value)
    )

export const addRoleSchemas = () => {
  const shape = {
    name: Yup.string()
      .trim()
      .required(getFieldRequiredMessage())
      .min(2, getRoleNameMinLengthMessage())
      .max(ROLE_NAME_MAX_LENGTH, getRoleNameMaxLengthMessage(ROLE_NAME_MAX_LENGTH))
      .matches(roleNameEnglishPattern, getRoleNameInvalidCharsMessage())
      .matches(noParenthesesPattern, getNameNoParenthesesMessage())
      .test(
        "role-name-has-letter",
        getRoleNameMustHaveLetterMessage(),
        value => /[a-zA-Z]/.test(value || "")
      ),
    nameAr: Yup.string()
      .trim()
      .required(getFieldRequiredMessage())
      .min(2, getRoleNameMinLengthMessage())
      .max(ROLE_NAME_MAX_LENGTH, getRoleNameMaxLengthMessage(ROLE_NAME_MAX_LENGTH))
      .matches(arabicNameOrNumberPattern, getValidArabicMessage())
      .matches(noParenthesesPattern, getNameNoParenthesesMessage())
      .test(
        "role-nameAr-has-letter",
        getRoleNameArabicMustHaveLetterMessage(),
        value => arabicLetterPattern.test(value || "")
      ),
    description: roleDescriptionField(),
    descriptionAr: arabicDescriptionField(),
  }

  return createSchemaShape(shape)
}

export const addUserSchemas = (isSuperAdminSession = false) => {
  const shape = {
    firstName: personNameField(),
    email: buildEmailYupSchema(),
    password: Yup.string()
      .required(getFieldRequiredMessage())
      .max(PASSWORD_MAX_LENGTH, getMaxPasswordMessage())
      .test(
        "password-strength",
        getPasswordStrengthMessage(),
        value => !value || isStrongPassword(value)
      ),
    lastName: personNameField(),
    mobile: phoneNumberField(),
    roles: Yup.object().required(getFieldRequiredMessage()),
    locations: isSuperAdminSession
      ? Yup.array()
      : Yup.array().when("allBranches", {
          is: true,
          then: schema => schema.notRequired(),
          otherwise: schema => schema.required(getFieldRequiredMessage()),
        }),
  }

  return createSchemaShape(shape)
}
export const editUserSchemas = (isSuperAdminSession = false) => {
  const shape = {
    firstName: personNameField(),
    lastName: personNameField(),
    email: buildEmailYupSchema(),
    roles: Yup.object().required(getFieldRequiredMessage()),
    mobile: phoneNumberField(),
    locations: isSuperAdminSession
      ? Yup.array()
      : Yup.array().when("allBranches", {
          is: true,
          then: schema => schema.notRequired(),
          otherwise: schema => schema.required(getFieldRequiredMessage()),
        }),
  }

  return createSchemaShape(shape)
}

export const resetPasswordSchema = () => {
  const shape = {
    email: buildEmailYupSchema(),
  }

  return createSchemaShape(shape)
}
export const verifyPasswordSchema = () => {
  const shape = {
    newPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .min(6, getMin6PasswordMessage()),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const userResetPasswordSchema = () => {
  const shape = {
    password: Yup.string().required(getFieldRequiredMessage()),
    newPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .min(6, getMin6PasswordMessage()),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const resetPasswordSchemaForUser = () => {
  const shape = {
    newPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .min(6, getMin6PasswordMessage()),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const updatePasswordSchema = () => {
  const shape = {
    newPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .min(6, getMin6PasswordMessage()),
    confirmPassword: Yup.string()
      .required(getFieldRequiredMessage())
      .oneOf([Yup.ref("newPassword"), null], getPasswordMatchMessage()),
  }

  return createSchemaShape(shape)
}

export const verifyOTPSchema = () => {
  const shape = {
    otp: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addBannerSetSchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    brand: Yup.object()
      .required(getFieldRequiredMessage())
      .test(
        "not-empty",
        getFieldRequiredMessage(),
        value => value && Object.keys(value).length > 0
      ),
  }

  return createSchemaShape(shape)
}

export const addSlideSchema = () => {
  const uploadedImageValue = () =>
    Yup.mixed()
      .nullable()
      .test("is-not-empty", getFieldRequiredMessage(), value => {
        if (value instanceof File || value instanceof Blob) return true
        return typeof value === "string" && value.trim().length > 0
      })

  let shape = {
    // imageType: Yup.string().required(getFieldRequiredMessage()),
    // imageURL: Yup.string().when("imageType", {
    //   is: "URL",
    //   then: Yup.string().url("Invalid URL").required(getFieldRequiredMessage()),
    // }),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageFile: Yup.mixed().when("imageType", {
      is: val => val === "UPLOAD",
      then: () => uploadedImageValue(),
      otherwise: schema => schema.notRequired(),
    }),
    imageURL: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          exactWidth: bannerWidth,
          exactHeight: bannerHeight,
        }),
      otherwise: schema => schema.notRequired().nullable(),
    }),
    imageUrlAr: Yup.string().when(
      ["applySameImageOnBothLang", "imageUrlTypeAr"],
      {
        is: (sameLang, type) => sameLang !== true && type === "URL",
        then: () =>
          imageUrlField({
            required: true,
            exactWidth: bannerWidth,
            exactHeight: bannerHeight,
          }),
        otherwise: schema => schema.notRequired().nullable(),
      }
    ),
    imageAr: Yup.mixed().when(
      ["applySameImageOnBothLang", "imageUrlTypeAr"],
      {
        is: (sameLang, type) => sameLang !== true && type === "UPLOAD",
        then: () => uploadedImageValue(),
        otherwise: schema => schema.notRequired().nullable(),
      }
    ),
    webImageType: Yup.string().required(getFieldRequiredMessage()),
    webImageFile: Yup.mixed().when("webImageType", {
      is: val => val === "UPLOAD",
      then: () => uploadedImageValue(),
      otherwise: schema => schema.notRequired(),
    }),
    webImageURL: Yup.string().when("webImageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          exactWidth: webBannerWidth,
          exactHeight: webBannerHeight,
        }),
      otherwise: schema => schema.notRequired().nullable(),
    }),
    webImageUrlAr: Yup.string().when(
      ["applySameWebImageOnBothLang", "webImageUrlTypeAr"],
      {
        is: (sameLang, type) => sameLang !== true && type === "URL",
        then: () =>
          imageUrlField({
            required: true,
            exactWidth: webBannerWidth,
            exactHeight: webBannerHeight,
          }),
        otherwise: schema => schema.notRequired().nullable(),
      }
    ),
    webImageAr: Yup.mixed().when(
      ["applySameWebImageOnBothLang", "webImageUrlTypeAr"],
      {
        is: (sameLang, type) => sameLang !== true && type === "UPLOAD",
        then: () => uploadedImageValue(),
        otherwise: schema => schema.notRequired().nullable(),
      }
    ),
    title: englishOptionalTitleField(),
    titleAr: arabicOptionalTitleField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    linkLabelAr: arabicOptionalTitleField(),
    externalLink: Yup.string().when("linkType", {
      is: val => val === "EXTERNAL_LINK",
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .url(i18next.t("Please enter a valid URL")),
      otherwise: schema => schema.notRequired(),
    }),
    duration: Yup.number()
      .typeError(i18next.t("Duration must be a number"))
      .min(0, i18next.t("Duration must be 0 or greater"))
      .integer(i18next.t("Duration must be a whole number"))
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? 0 : value
      ),
    webTitle: englishOptionalTitleField(),
    webTitleAr: arabicOptionalTitleField(),
    webDescription: englishDescriptionField(),
    webDescriptionAr: arabicDescriptionField(),
    webLinkLabelAr: arabicOptionalTitleField(),
    webExternalLink: Yup.string().when("webLinkType", {
      is: val => val === "EXTERNAL_LINK",
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .url(i18next.t("Please enter a valid URL")),
      otherwise: schema => schema.notRequired(),
    }),
    webDuration: Yup.number()
      .typeError(i18next.t("Duration must be a number"))
      .min(0, i18next.t("Duration must be 0 or greater"))
      .integer(i18next.t("Duration must be a whole number"))
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? 0 : value
      ),
    displayOrder: integerDisplayOrderField(),
    webDisplayOrder: integerDisplayOrderField(),
    // imageFile: Yup.mixed().when("imageType", {
    //   is: "UPLOAD",
    //   then: Yup.mixed().required("Image file is required"),
    // }),
  }

  return createSchemaShape(shape)
}

export const addCategoriesSchemas = mode => {
  let shape = {
    nameAr: arabicNameField(),
    descriptionAr: arabicDescriptionField(),
    descriptionEn: englishDescriptionField(),
    nameEn: englishNameField(),
    displayOrder: integerDisplayOrderField(),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    menu: Yup.object().required(getFieldRequiredMessage()),
    brand: Yup.object().required(getFieldRequiredMessage()),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          requireSquare: true,
          recommendedWidth: categoryWidth,
          recommendedHeight: categoryHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: true }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  } else {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: false }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addItemSchemas = mode => {
  let shape = {
    nameAr: arabicNameField(),
    descriptionAr: arabicDescriptionField(),
    descriptionEn: englishDescriptionField(),
    nameEn: englishNameField(),
    displayOrder: Yup.number()
      .typeError(getDisplayNumMessage())
      .integer(getWholeNumberMessage())
      .min(0, getGreaterOrEqual0())
      .max(
        9999,
        i18next.t("Please enter a valid number up to {{value}} digits", {
          value: 4,
        })
      )
      .required(getFieldRequiredMessage()),
    itemPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d{1,18}(\.\d{1,2})?$/, getValidNumMessage()),
    originalPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d{1,18}(\.\d{1,2})?$/, getValidNumMessage()),
    personCountPerServing: personCountPerServingField(),
    caloriesForCalculations: Yup.string()
      .nullable()
      .matches(
        /^\d{0,10}$/,
        i18next.t("Please enter a valid number up to {{value}} digits", {
          value: 10,
        })
      ),
    SKU: Yup.string()
      .nullable()
      .test(
        "sku-non-negative",
        getGreaterOrEqual0(),
        value => {
          if (value === null || value === undefined || value === "") return true
          const str = String(value).trim()
          // Allow alphanumeric SKUs; only reject if purely numeric and negative
          if (/^-/.test(str)) return false
          return true
        }
      ),
    // weight: optionalNonNegativeNumberField(),
    // width: optionalNonNegativeNumberField(),
    // height: optionalNonNegativeNumberField(),
    // length: optionalNonNegativeNumberField(),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          exactWidth: itemWidth,
          exactHeight: itemHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
    category: Yup.string().required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),

    minimum: requiredNonNegativeNumberField(),

    maximum: requiredNonNegativeNumberField().test(
      "max-greater-than-min",
      i18next.t("Maximum must be greater than or equal to Minimum"),
      function (value) {
        const { minimum } = this.parent
        if (!minimum || !value) return true // skip if either is empty
        return parseFloat(value) >= parseFloat(minimum)
      }
    ),
  }

  if (mode === "add" || mode === "allItemsAdd") {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: true }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  } else {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: false }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addComboSchemas = mode => {
  let shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    displayOrder: integerDisplayOrderField(),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),

    discount: Yup.string().when("isDiscountAllowed", {
      is: val => val === true,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^\d+$/, getValidNumMessage())
          .test("min-max-values", getValue0To100(), value => {
            const number = parseInt(value, 10)
            return number >= 0 && number <= 100
          }),
      otherwise: schema => schema.notRequired(),
    }),

    originalPrice: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    personCountPerServing: personCountPerServingField(),
    // calorieCount: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .matches(/^\d+$/, getOnlyNumMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          requireSquare: true,
          recommendedWidth: itemWidth,
          recommendedHeight: itemHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
    startDateAndTime: Yup.mixed().when("hasSpecificDateAndTime", {
      is: val => val === true,
      then: () => Yup.date().required(getFieldRequiredMessage()).nullable(),
      otherwise: schema => schema.notRequired().nullable(),
    }),
    endDateAndTime: Yup.mixed().when("hasSpecificDateAndTime", {
      is: val => val === true,
      then: () =>
        Yup.date()
          .required(getFieldRequiredMessage())
          .nullable()
          .test("is-greater", getExpiredDateMsg(), function (value) {
            const { startDateAndTime } = this.parent
            if (!value || !startDateAndTime) return true
            return new Date(value) > new Date(startDateAndTime)
          }),
      otherwise: schema => schema.notRequired().nullable(),
    }),

    categoryId: Yup.object().required(getFieldRequiredMessage()),
    items: Yup.array()
      .of(
        Yup.object().shape({
          item: Yup.object().nullable().required(getFieldRequiredMessage()),
          quantity: Yup.number()
            .typeError(getQtyNum())
            .integer(getQtyInteger())
            .min(0, getGreaterOrEqual0())
            .positive(getQtyPositive())
            .required(getFieldRequiredMessage()),
          modifiers: Yup.array().of(
            Yup.object().shape({
              modifier: Yup.array().of(Yup.object()).nullable(),
            })
          ),
        })
      )
      .min(1, getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    isItemGroup: Yup.boolean(),
    isPriceCustomize: Yup.boolean(),
    selectedGroupItems: Yup.array().when(["isItemGroup", "isPriceCustomize"], {
      is: (isItemGroup, isPriceCustomize) =>
        Boolean(isItemGroup) && Boolean(isPriceCustomize),
      then: () =>
        Yup.array()
          .of(
            Yup.object().shape({
              price: Yup.number()
                .transform((value, originalValue) => {
                  if (
                    originalValue === "" ||
                    originalValue === null ||
                    originalValue === undefined
                  ) {
                    return undefined
                  }
                  return Number(originalValue)
                })
                .typeError(getValidNumMessage())
                .required(getFieldRequiredMessage())
                .moreThan(0, i18next.t("Amount must be greater than 0"))
                .test(
                  "customize-price-two-decimals",
                  getValidNumMessage(),
                  value => {
                    if (value === undefined || value === null) return true
                    const cents = Math.round(Number(value) * 100)
                    return Math.abs(Number(value) * 100 - cents) < 1e-6
                  }
                ),
            })
          )
          .min(1, getFieldRequiredMessage()),
      otherwise: () => Yup.array().notRequired(),
    }),
  }
  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }
  return createSchemaShape(shape)
}

export const addModifierSchemas = mode => {
  let shape = {
    nameAr: arabicNameField(),
    nameEn: englishNameField(),
    // item: Yup.string().required(getFieldRequiredMessage()),
    displayOrder: integerDisplayOrderField(),
    type: Yup.string().required(getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    items: Yup.array().of(
      Yup.object({
        itemId: Yup.object().required(getFieldRequiredMessage()),
        minQuantity: Yup.number().when("type", {
          is: val => val === "sizes",
          then: () =>
            Yup.number()
              .typeError(getOnlyNumMessage())
              .integer(getQtyInteger())
              .test("is-one", getMinExact1(), value => value === 1)
              .required(getFieldRequiredMessage()),
          otherwise: schema =>
            Yup.number()
              .typeError(getDisplayNumMessage())
              .integer(getQtyInteger())
              .required(getFieldRequiredMessage())
              .min(0, getMinimumNegMessage()),
        }),
        maxQuantity: Yup.number().when("type", {
          is: val => val === "sizes",
          then: () =>
            Yup.number()
              .typeError(getOnlyNumMessage())
              .integer(getQtyInteger())
              .test("is-one", getMaxExact1(), value => value === 1)
              .required(getFieldRequiredMessage()),
          otherwise: schema =>
            Yup.number()
              .typeError(getDisplayNumMessage())
              .integer(getQtyInteger())
              .required(getFieldRequiredMessage())
              .min(Yup.ref("minQuantity"), getMaxMustGreaterMin()),
        }),
      })
    ),
    // minimum: Yup.number().when("type", {
    //   is: val => val === "sizes",
    //   then: () =>
    //     Yup.number()
    //       .typeError(getOnlyNumMessage())
    //       .test("is-one", getMinExact1(), value => value === 1)
    //       .required(getFieldRequiredMessage()),
    //   otherwise: schema =>
    //     Yup.number()
    //       .typeError(getDisplayNumMessage())
    //       .required(getFieldRequiredMessage())
    //       .min(0, getMinimumNegMessage()),
    // }),

    // maximum: Yup.number().when("type", {
    //   is: val => val === "sizes",
    //   then: () =>
    //     Yup.number()
    //       .typeError(getOnlyNumMessage())
    //       .test("is-one", getMaxExact1(), value => value === 1)
    //       .required(getFieldRequiredMessage()),
    //   otherwise: schema =>
    //     Yup.number()
    //       .typeError(getDisplayNumMessage())
    //       .required(getFieldRequiredMessage())
    //       .min(Yup.ref("minimum"), getMaxMustGreaterMin()),
    // }),

    // maxModifiersOptions: Yup.number().typeError(
    //   getDisplayNumMessage()
    // ),
    // freeModifiersCount: Yup.number().typeError(
    //   getDisplayNumMessage()
    // ),

    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          requireSquare: true,
          recommendedWidth: modifierGroupWidth,
          recommendedHeight: modifierGroupHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "add" || mode === "allModifierAdd") {
    shape = {
      ...shape,
      imageFile: Yup.string().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => Yup.string().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addModifierOptionsSchemas = mode => {
  let shape = {
    nameAr: arabicNameField(),
    nameEn: englishNameField(),
    caloriesForCalculations: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    minimum: Yup.number()
      .typeError(getOnlyNumMessage())
      .integer(getQtyInteger())
      .required(getFieldRequiredMessage())
      .min(0, getMinimumNegMessage()),
    maximum: Yup.number()
      .typeError(getOnlyNumMessage())
      .integer(getQtyInteger())
      .min(Yup.ref("minimum"), getMaxMustGreaterMin())
      .required(getFieldRequiredMessage()),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () =>
        imageUrlField({
          required: true,
          requireSquare: true,
          recommendedWidth: modifierOptionWidth,
          recommendedHeight: modifierOptionHeight,
        }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "allModifierAdd") {
    shape = {
      ...shape,
      modifierGroup: Yup.object()
        .shape({
          value: Yup.string().required(getFieldRequiredMessage()),
          label: Yup.string().required(getFieldRequiredMessage()),
        })
        .required(getFieldRequiredMessage()),
    }
  }

  if (mode === "add" || mode === "allModifierAdd") {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: true }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  } else {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: false }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addItemGroupSchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    minimum: Yup.number()
      .typeError(getOnlyNumMessage())
      .required(getFieldRequiredMessage())
      .min(0, getFieldRequiredMessage()),
    maximum: Yup.number()
      .typeError(getOnlyNumMessage())
      .min(Yup.ref("minimum"), getMaxMustGreaterMin())
      .required(getFieldRequiredMessage()),
    quantity: Yup.number()
      .typeError(getQtyNum())
      .integer(getQtyInteger())
      .min(0, getGreaterOrEqual0())
      .positive(getQtyPositive())
      .required(getFieldRequiredMessage()),
    items: Yup.array().min(1, getMinOneItemGroup()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
  }
  return createSchemaShape(shape)
}

export const itemAdvanceSettingSchema = () => {
  const shape = {
    category: Yup.string().required(getFieldRequiredMessage()),
    personCountPerServing: personCountPerServingField(),
  }

  return createSchemaShape(shape)
}

export const taxSchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    rate: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const itemVideoValidation = () => {
  const shape = {
    videoUrlType: Yup.string().required(getFieldRequiredMessage()),
    videoUrl: Yup.string().when("videoUrlType", {
      is: val => val === "URL",
      then: () => Yup.mixed().required(getFieldRequiredMessage()),
    }),
    video: Yup.string().when("videoUrlType", {
      is: val => val === "UPLOAD",
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const editCustomerSchema = () => {
  const shape = {
    firstName: personNameField(),
    lastName: personNameField(),
    // phoneCode: Yup.string().required(getFieldRequiredMessage()),
    // phoneNumber: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .matches(/^\d+$/, "Please enter a valid phone number"),
    email: buildEmailYupSchema(),
  }

  return createSchemaShape(shape)
}

export const addPromotionSchema = () =>
  Yup.lazy(values => {
    const offerType = values?.offerType
    var shape = {
    name: Yup.string()
      .trim()
      .required(getFieldRequiredMessage())
      .max(
        ENTITY_NAME_MAX_LENGTH,
        i18next.t("Name cannot exceed 255 characters")
      )
      .test(
        "no-unsafe-content",
        getBrandTextUnsafeContentMessage(),
        value => !value || !containsUnsafeTextContent(value)
      )
      .matches(
        /^[a-zA-Z0-9\s\-'()%&.,!]+$/,
        i18next.t(
          "Discount name may only contain letters, numbers, spaces, and - ' ( ) % & . , !"
        )
      )
      .test(
        "discount-name-has-letter",
        getEnglishNameMustHaveLetterMessage(),
        value => englishNameLetterPattern.test(value || "")
      ),
    offerType: Yup.string().required(getFieldRequiredMessage()),
    amount: Yup.number()
      .typeError(getDisplayNumMessage())
      .moreThan(0, i18next.t("Amount must be greater than 0"))
      .required(() => getFieldRequiredMessage()),
    countryCode: Yup.object().required(getFieldRequiredMessage()),
    reference: Yup.object().nullable().notRequired(),

    cartRules: Yup.array().of(
      Yup.object().shape({
        category: Yup.object().required(getFieldRequiredMessage()),
        item: Yup.object().required(getFieldRequiredMessage()),
        quantity: Yup.number()
          .typeError(getFieldRequiredMessage())
          .required(getFieldRequiredMessage())
          .positive(getQtyPositive()),
      })
    ),

    limits: Yup.object().shape({
      maxUsageOfCustomers: Yup.number().when("isMaxUsageOfCustomers", {
        is: val => val === true,
        then: () => Yup.number().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
      maxUsagePerCustomer: Yup.number().when("isMaxUsagePerCustomer", {
        is: val => val === true,
        then: () => Yup.number().required(getFieldRequiredMessage()),
        otherwise: schema => schema.notRequired(),
      }),
    }),

    minimumCartAmount: Yup.number()
      .nullable()
      .transform((value, original) =>
        original === "" || original === null || original === undefined
          ? null
          : value
      )
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .integer(getWholeNumberMessage())
      .notRequired(),

    maximumCartAmount: Yup.number()
      .nullable()
      .transform((value, original) =>
        original === "" || original === null || original === undefined
          ? null
          : value
      )
      .typeError(getDisplayNumMessage())
      .min(0, getMinimumNegMessage())
      .integer(getWholeNumberMessage())
      .notRequired()
      .test(
        "max-gte-min-cart",
        i18next.t(
          "Maximum cart amount must be greater than or equal to minimum cart amount"
        ),
        function (value) {
          const { minimumCartAmount } = this.parent
          if (
            value == null ||
            minimumCartAmount == null ||
            Number.isNaN(Number(value)) ||
            Number.isNaN(Number(minimumCartAmount))
          ) {
            return true
          }
          return Number(value) >= Number(minimumCartAmount)
        }
      ),

    discountLimits: Yup.object().shape({
      maxDiscountAmountPerCustomer: Yup.number().when(
        "isMaxDiscountAmountPerCustomer",
        {
          is: val => val === true,
          then: () => Yup.number().required(getFieldRequiredMessage()),
          otherwise: schema => schema.notRequired(),
        }
      ),
    }),
  }
  if (offerType === "category") {
    shape = {
      ...shape,
      qualificationItems: Yup.array()
        .of(
          Yup.object().shape({
            category: Yup.object().required(getFieldRequiredMessage()),
          })
        )
        .min(1, getFieldRequiredMessage())
        .test(
          "unique-categories",
          i18next.t("Duplicate categories are not allowed"),
          items => {
            if (!items?.length) return true
            const ids = items
              .map(item => item?.category?.value)
              .filter(Boolean)
              .map(String)
            return new Set(ids).size === ids.length
          }
        ),
    }
  }
  if (offerType === "item") {
    shape = {
      ...shape,
      qualificationItems: Yup.array()
        .of(
          Yup.object().shape({
            category: Yup.object().required(getFieldRequiredMessage()),
            item: Yup.object().required(getFieldRequiredMessage()),
          })
        )
        .min(1, getFieldRequiredMessage())
        .test(
          "unique-items",
          i18next.t("Duplicate items are not allowed"),
          items => {
            if (!items?.length) return true
            const ids = items
              .map(item => item?.item?.value)
              .filter(Boolean)
              .map(String)
            return new Set(ids).size === ids.length
          }
        ),
    }
  }

  return Yup.object().shape(shape)
  })

/** Max length for coupon name fields (English and Arabic). */
export const COUPON_NAME_MAX_LENGTH = 255
/** Max length for bulk coupon prefix (alphanumeric, no spaces). */
export const BULK_COUPON_PREFIX_MAX_LENGTH = 10
/** Coupon code length bounds (manual + CSV bulk). */
export const COUPON_CODE_MIN_LENGTH = 3
export const COUPON_CODE_MAX_LENGTH = 10

/** Coupon / discount-style English names: letters, digits, spaces, and - ' ( ) % & . , ! */
const couponEnglishNamePattern = /^[a-zA-Z0-9\s\-'()%&.,!]+$/
/** Coupon Arabic names: Arabic letters, digits, spaces, and matching punctuation incl. %. */
const couponArabicNamePattern =
  /^[\u0621-\u064A0-9\s\-'()%&.,!\u060C\u061B\u061F]+$/

const couponEnglishNameField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(
      COUPON_NAME_MAX_LENGTH,
      getCouponNameMaxLengthMessage(COUPON_NAME_MAX_LENGTH)
    )
    .matches(
      couponEnglishNamePattern,
      i18next.t(
        "Coupon name may only contain letters, numbers, spaces, and - ' ( ) % & . , !"
      )
    )
    .test(
      "english-name-has-letter",
      getEnglishNameMustHaveLetterMessage(),
      value => englishNameLetterPattern.test(value || "")
    )
    .test(
      "no-unsafe-content",
      getBrandTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )

const couponArabicNameField = () =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(
      COUPON_NAME_MAX_LENGTH,
      getCouponNameMaxLengthMessage(COUPON_NAME_MAX_LENGTH)
    )
    .matches(
      couponArabicNamePattern,
      i18next.t(
        "Coupon Arabic name may only contain Arabic letters, numbers, spaces, and - ' ( ) % & . , !"
      )
    )
    .test(
      "arabic-name-has-letter",
      getArabicNameMustHaveLetterMessage(),
      value => arabicLetterPattern.test(value || "")
    )

export const addCouponGroupSchema = () => {
  const shape = {
    name: couponEnglishNameField(),
    nameAr: couponArabicNameField(),
  }
  return createSchemaShape(shape)
}

const couponPromotionField = () =>
  Yup.object().required(getFieldRequiredMessage())

const couponStartsAtField = () =>
  Yup.string().required(getFieldRequiredMessage())

const couponExpiresAtField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .test("is-greater", getExpiredDateMsg(), function (value) {
      const { startsAt } = this.parent
      return new Date(value) > new Date(startsAt)
    })

const couponMaxUsageField = () =>
  Yup.number().when("allowInfiniteUsage", {
    is: val => val === false,
    then: () =>
      Yup.number()
        .typeError(getDisplayNumMessage())
        .required(getFieldRequiredMessage())
        .integer(getQtyInteger())
        .min(1, getMinimumCountMessage()),
    otherwise: schema => schema.notRequired(),
  })

const couponCodeField = () =>
  Yup.string()
    .required(getFieldRequiredMessage())
    .matches(/^[a-zA-Z0-9]+$/, getAlfaNumericChar())
    .min(COUPON_CODE_MIN_LENGTH, getCouponCodeLengthMessage())
    .max(COUPON_CODE_MAX_LENGTH, getCouponCodeLengthMessage())
    .trim()

export const addCouponSchema = mode => {
  const imageRequired = mode === "digitalAdd"
  const shape = {
    name: couponEnglishNameField(),
    nameAr: couponArabicNameField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    code: couponCodeField(),
    promotion: couponPromotionField(),
    startsAt: couponStartsAtField(),
    expiresAt: couponExpiresAtField(),
    maxUsage: couponMaxUsageField(),
    customers: Yup.array().when("isLimitToSpecificCustomer", {
      is: val => val === true,
      then: () =>
        Yup.array()
          .min(1, getMinOneCustomer())
          .required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => imageUrlField({ required: imageRequired }),
      otherwise: schema => schema.notRequired(),
    }),
    imageFile: Yup.mixed().when("imageType", {
      is: val => val === "UPLOAD",
      then: () => imageFileField({ required: imageRequired }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const addBulkCouponSchema = () => {
  const shape = {
    name: couponEnglishNameField(),
    nameAr: couponArabicNameField(),
    promotion: couponPromotionField(),
    couponGroup: Yup.object().required(getFieldRequiredMessage()),
    startsAt: couponStartsAtField(),
    expiresAt: couponExpiresAtField(),
    file: Yup.mixed().when("hasUpload", {
      is: val => val === true,
      then: () =>
        Yup.mixed()
          .required(getFieldRequiredMessage())
          .test("fileType", getInvalidCsvFileMessage(), value => {
            if (!value) return false
            const name = value.name || ""
            return name.toLowerCase().endsWith(".csv")
          }),
      otherwise: schema => schema.notRequired(),
    }),
    prefix: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .max(
            BULK_COUPON_PREFIX_MAX_LENGTH,
            getBulkCouponPrefixMaxLengthMessage(BULK_COUPON_PREFIX_MAX_LENGTH)
          )
          .matches(/^[\da-zA-Z]+$/, getOnlyNumAndChar()),
      otherwise: schema => schema.notRequired(),
    }),
    noOfDigits: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^\d+$/, getOnlyNumMessage())
          .test("min-max-values", getValueBt3To10(), value => {
            const number = parseInt(value, 10)
            return number >= 3 && number <= 10
          }),
      otherwise: schema => schema.notRequired(),
    }),
    noOfCodes: Yup.string().when("hasUpload", {
      is: val => val == false,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .matches(/^[0-9]+$/, getOnlyNumMessage())
          .test("min-max-values", getValueBt1To1000(), value => {
            const number = parseInt(value, 10)
            return number >= 1 && number <= 1000
          }),
      otherwise: schema => schema.notRequired(),
    }),
    maxUsage: couponMaxUsageField(),
  }

  return createSchemaShape(shape)
}

export const addWalletCouponSchema = (mode, existingMaxCount) => {
  const maxCountValidation = Yup.number()
    .optional()
    .nullable()
    .transform((v, o) => (o === "" || o === null || o === undefined ? undefined : v))
    .min(1, getMinimumCountMessage())

  if (mode === "edit" && existingMaxCount != null && existingMaxCount !== "") {
    const minVal = Number(existingMaxCount)
    if (!isNaN(minVal)) {
      maxCountValidation.test(
        "min-existing",
        i18next.t("Max count cannot be less than existing value ({{count}})", {
          count: minVal,
        }),
        value => value == null || value === "" || value >= minVal
      )
    }
  }

  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    code: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[a-zA-Z0-9]+$/, getAlfaNumericChar())
      .trim(),
    walletAmount: Yup.number()
      .required(getFieldRequiredMessage())
      .typeError(getDisplayNumMessage())
      .min(1, getMinimumCountMessage()),
    startsAt: Yup.string().required(getFieldRequiredMessage()),
    expiresAt: Yup.string()
      .required(getFieldRequiredMessage())
      .test("is-greater", getExpiredDateMsg(), function (value) {
        const { startsAt } = this.parent
        return new Date(value) > new Date(startsAt)
      }),
    maxCount: maxCountValidation,
  }
  return createSchemaShape(shape)
}

export const addCouponRedeemSchema = addWalletCouponSchema

export const referralInfoSchema = () => {
  const shape = {
    earnedPointsForSender: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    earnedPointsForReceiver: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    pointValidityInDays: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const loyaltyPointInfoSchema = () => {
  const shape = {
    requiredPointsForOneSAR: loyaltyRequiredPointsField(),
    minLimitOfPointRedemtion: loyaltyRequiredPointsField(),
    maxLimitOfPointRedemtion: loyaltyOptionalPointsField().test(
      "max-greater-than-min",
      i18next.t("Maximum must be greater than or equal to Minimum"),
      function (value) {
        const { minLimitOfPointRedemtion } = this.parent
        // an empty maximum means the tenant wants no per order cap
        if (value === null || value === undefined || value === "") return true
        if (
          minLimitOfPointRedemtion === null ||
          minLimitOfPointRedemtion === undefined ||
          minLimitOfPointRedemtion === ""
        ) {
          return true
        }
        return Number(value) >= Number(minLimitOfPointRedemtion)
      }
    ),
    pointValidityInDays: loyaltyRequiredPointsField(),
    earningPointInMinutes: loyaltyRequiredPointsField(),
    requiredCollectPointsForOneSAR: loyaltyRequiredPointsField(),
    orderStatusForLoyaltyCredit: Yup.string().required(
      getFieldRequiredMessage()
    ),
  }

  return createSchemaShape(shape)
}

export const walletSettingsSchema = () => {
  const positiveAmount = () =>
    Yup.number()
      .typeError(i18next.t("Enter a valid amount"))
      .required(getFieldRequiredMessage())
      .positive(i18next.t("Enter a valid amount"))

  const shape = {
    minWalletTxnAmount: positiveAmount(),
    maxWalletTxnAmount: positiveAmount().min(
      Yup.ref("minWalletTxnAmount"),
      i18next.t("Maximum must be greater than or equal to Minimum")
    ),
    walletTxnAmountDenoms: Yup.array()
      .of(Yup.number().positive(i18next.t("Enter a valid amount")))
      .test(
        "within-range",
        i18next.t("Amount must be between minimum and maximum"),
        function (value) {
          const { minWalletTxnAmount, maxWalletTxnAmount } = this.parent
          const min = Number(minWalletTxnAmount)
          const max = Number(maxWalletTxnAmount)
          if (!Array.isArray(value) || value.length === 0) return true
          if (!Number.isFinite(min) || !Number.isFinite(max)) return true
          return value.every(
            amount => Number(amount) >= min && Number(amount) <= max
          )
        }
      ),
  }

  return createSchemaShape(shape)
}

export const revokeLoyaltyPointsSchema = () => {
  const shape = {
    points: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage())
      .test(
        "is-greater-than-zero",
        getGreaterOrEqual0(),
        value => parseInt(value) > 0
      ),

    remarks: loyaltyRemarkField(),
  }

  return createSchemaShape(shape)
}

/**
 * Push notification text: free text (digits, punctuation, emoji, ${PLACEHOLDER},
 * brand names in either script) except markup. Each field must include at least
 * one letter of its own language.
 */
const notificationTextField = (maxLength, letterPattern, letterMessage) =>
  Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .max(maxLength, getNotificationTextMaxLengthMessage(maxLength))
    .test(
      "no-unsafe-content",
      getNotificationTextUnsafeContentMessage(),
      value => !value || !containsUnsafeTextContent(value)
    )
    .test("has-language-letter", letterMessage, value =>
      letterPattern.test(value || "")
    )

const notificationEnglishTextField = maxLength =>
  notificationTextField(
    maxLength,
    englishNameLetterPattern,
    getNotificationTextEnglishLetterMessage()
  )

const notificationArabicTextField = maxLength =>
  notificationTextField(
    maxLength,
    arabicLetterPattern,
    getNotificationTextArabicLetterMessage()
  )

export const editNotificationSchema = () => {
  const shape = {
    titleEn: notificationEnglishTextField(ENTITY_NAME_MAX_LENGTH),
    titleAr: notificationArabicTextField(ENTITY_NAME_MAX_LENGTH),
    bodyEn: notificationEnglishTextField(DESCRIPTION_MAX_LENGTH),
    bodyAr: notificationArabicTextField(DESCRIPTION_MAX_LENGTH),
  }

  return createSchemaShape(shape)
}

export const sendNotificationSchema = () => {
  const shape = {
    titleEn: englishNameField(),
    titleAr: arabicNameField(),
    bodyEn: englishDescriptionField().required(getFieldRequiredMessage()),
    bodyAr: arabicDescriptionField().required(getFieldRequiredMessage()),

    scheduledTime: Yup.string().when("isScheduled", {
      is: val => val === true,
      then: () => Yup.string().required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
    customers: Yup.array().when("type", {
      is: val => val === "specific-customers",
      then: () =>
        Yup.array()
          .min(1, getMinOneCustomer())
          .required(getFieldRequiredMessage()),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

const countryDetailsFieldsShape = () => ({
  name: countryNameEnglishField(),
  nameAr: countryNameArabicField(),
  dialCode: Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .matches(/^\+[0-9]{1,4}$/, getDialCodeFormatMessage()),
  countryCode: Yup.string()
    .trim()
    .required(getFieldRequiredMessage())
    .matches(/^[A-Za-z]{2,3}$/, getCountryCodeFormatMessage()),
  displayOrder: Yup.string()
    .required(getFieldRequiredMessage())
    .matches(/^[0-9]+$/, getOnlyNumMessage()),
  phoneNumberLength: Yup.string()
    .required(getFieldRequiredMessage())
    .matches(/^[0-9]+$/, getOnlyNumMessage())
    .test("min-one", getMinimumCountMessage(), value => {
      if (!value) return true
      return Number(value) >= 1
    })
    .test("safe-number", getSafeNumberLimitMessage(), value => {
      if (!value) return true
      const number = Number(value)
      return Number.isFinite(number) && number <= NUMERIC_SAFE_MAX
    }),
  currencySymbol: Yup.string().required(getFieldRequiredMessage()),
  currency: Yup.string().required(getFieldRequiredMessage()),
})

const countryTaxShipmentFieldsShape = () => ({
  title: Yup.string().required(getFieldRequiredMessage()),
  titleAr: Yup.string()
    .required(getFieldRequiredMessage())
    .matches(arabicNameOrNumberPattern, getValidArabicMessage()),
  value: Yup.string()
    .required(getFieldRequiredMessage())
    .matches(/^[0-9]+$/, getOnlyNumMessage())
    .test("min-max-values", getValue0To100(), value => {
      const number = parseInt(value, 10)
      return number >= 1 && number <= 100
    }),
  registeredNo: Yup.string().required(getFieldRequiredMessage()),
  // Shipment Details temporarily disabled — not used in live delivery fee logic.
  // regionType: Yup.string().required(getFieldRequiredMessage()),
  //
  // fixedDeliveryCharge: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^\d+(\.\d+)?$/, getOnlyNumMessage()),
  //
  // freeDeliveryThreshold: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // enableFreeDelivery: Yup.boolean(),
  //
  // actualBaseWeightLimit: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // actualBaseRate: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // actualExtraPerKgRate: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  // dynamicBaseWeightLimit: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // dynamicBaseRate: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // dynamicExtraPerKgRate: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // enableBoxFee: Yup.boolean(),
  //
  // boxFee: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // noOfBaseItemsLimit: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // baseFulfillmentFee: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // extraItemFee: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // enableSurcharge: Yup.boolean(),
  //
  // surchargeRate: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // enableDynamicCalc: Yup.boolean(),
  //
  // volumetricDivisor: Yup.string()
  //   .required(getFieldRequiredMessage())
  //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
  //
  // priorityLogic: Yup.string().required(getFieldRequiredMessage()),
})

/** Platform country CRUD — basic identity fields only. */
export const platformCountrySchema = () => {
  return createSchemaShape(countryDetailsFieldsShape())
}

/** Tenant tax + shipment settings for an assigned country. */
export const tenantCountryTaxShipmentSchema = () => {
  return createSchemaShape(countryTaxShipmentFieldsShape())
}

export const addCountrySchema = () => {
  return createSchemaShape({
    ...countryDetailsFieldsShape(),
    ...countryTaxShipmentFieldsShape(),
  })
}

export const addRegionSchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    country: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addCitySchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    country: Yup.object().required(getFieldRequiredMessage()),
    region: Yup.object().required(getFieldRequiredMessage()),
    displayOrder: integerDisplayOrderField(),
  }

  return createSchemaShape(shape)
}
export const addZoneSchema = (options = {}) => {
  const {
    existingZones = [],
    excludeZoneId = null,
    requireDeliveryDays = true,
  } = options
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    city: Yup.object().required(getFieldRequiredMessage()),
    minDays: requireDeliveryDays
      ? requiredNonNegativeWholeNumberField()
      : Yup.mixed().nullable().notRequired(),
    maxDays: requireDeliveryDays
      ? requiredNonNegativeWholeNumberField()
      : Yup.mixed().nullable().notRequired(),
    minOrderAmount: requiredZoneAmountField(),
    deliveryCharges: requiredZoneAmountField(),
    freeDeliveryOrderAmount: Yup.string().when("deliveryCharges", {
      is: value => Number(value) > 0,
      then: () =>
        Yup.string()
          .required(getFieldRequiredMessage())
          .test(
            "greater-than-zero",
            i18next.t("Value must be greater than 0"),
            value => {
              if (
                value === null ||
                value === undefined ||
                String(value).trim() === ""
              ) {
                return true
              }
              const str = String(value).trim()
              if (isNegativeNumberValue(str)) return false
              if (!/^\d+(\.\d{1,2})?$/.test(str)) return true
              return Number(str) > 0
            }
          )
          .matches(/^\d+(\.\d{1,2})?$/, getValidNumMessage()),
      otherwise: schema =>
        schema
          .nullable()
          .notRequired()
          .test(
            "optional-non-negative",
            getGreaterOrEqual0(),
            value => !isNegativeNumberValue(value)
          ),
    }),
    deliveryEnabled: Yup.boolean(),
    boundary: Yup.mixed()
      .nullable()
      .test("boundary-valid", function (value) {
        const ring = value?.coordinates?.[0]
        if (!ring?.length) {
          return true
        }
        if (ring.length < 4 || !isPolygonRingClosed(ring)) {
          return this.createError({
            message: i18next.t(
              "Delivery boundary is invalid. Draw a closed polygon with at least 3 points."
            ),
          })
        }
        if (hasRingSelfIntersection(ring)) {
          return this.createError({
            message: i18next.t(
              "Delivery boundary is invalid because polygon edges cross. Redraw the boundary without overlapping lines."
            ),
          })
        }
        const overlappingZone = findOverlappingBoundary(
          value,
          existingZones,
          excludeZoneId
        )
        if (overlappingZone) {
          const zoneName =
            i18next.language === "sa"
              ? overlappingZone.nameAr || overlappingZone.name
              : overlappingZone.name || overlappingZone.nameAr
          return this.createError({
            message: i18next.t(
              'This delivery boundary overlaps with zone "{{name}}". Adjust the boundary so zones do not overlap.',
              { name: zoneName }
            ),
          })
        }
        return true
      }),
  }

  return createSchemaShape(shape)
}

export const addIssueSchema = () => {
  const shape = {
    title: englishNameField(),
    titleAr: arabicNameField(),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const updateOpeningHoursSchema = () => {
  const shape = {
    ids: Yup.array()
      .min(1, getMinOneLocation())
      .required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addIngredientSchema = mode => {
  let shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    price: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => imageUrlField({ required: true }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: true }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  } else {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: false }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addItemTagSchema = () => {
  return createSchemaShape({
    name: englishNameField(),
    nameAr: arabicNameField(),
    displayOrder: integerDisplayOrderField(),
  })
}

export const addAllergenSchema = mode => {
  let shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    imageType: Yup.string().required(getFieldRequiredMessage()),
    imageUrl: Yup.string().when("imageType", {
      is: val => val === "URL",
      then: () => imageUrlField({ required: true }),
      otherwise: schema => schema.notRequired(),
    }),
  }

  if (mode === "add") {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: true }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  } else {
    shape = {
      ...shape,
      imageFile: Yup.mixed().when("imageType", {
        is: val => val === "UPLOAD",
        then: () => imageFileField({ required: false }),
        otherwise: schema => schema.notRequired().nullable(),
      }),
    }
  }

  return createSchemaShape(shape)
}

export const addLoyaltyPointSchema = mode => {
  let shape = {
    points: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    remarks: loyaltyRemarkField(),
    expiresAt: loyaltyExpiresAtField(),
  }
  if (mode === "GLOBAL") {
    shape = {
      ...shape,
      customers: Yup.array()
        .min(1, getMinOneCustomer())
        .required(getFieldRequiredMessage()),
    }
  }

  return createSchemaShape(shape)
}

export const copyBranchesSchema = () => {
  const shape = {
    ids: Yup.array()
      .min(1, getMinOneLocation())
      .required(getFieldRequiredMessage()),
    settings: Yup.array()
      .min(1, getMinOneSetting())
      .required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addFaqSchema = () => {
  const shape = {
    title: englishQuestionField(),
    titleAr: arabicQuestionField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
  }

  return createSchemaShape(shape)
}

export const generateQrCodeSchema = () => {
  const shape = {
    branchId: Yup.object().required(getFieldRequiredMessage()),
    type: Yup.object().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addQuestionSchema = () => {
  const shape = {
    en: englishQuestionField(),
    ar: arabicQuestionField(),
    type: Yup.string().required(getFieldRequiredMessage()),

    // scale: Yup.string().when("type", {
    //   is: val => val == "0",
    //   then: () =>
    //     Yup.string()
    //       .required(getFieldRequiredMessage())
    //       .matches(/^\d+$/, getValidNumMessage())
    //       .test(
    //         "min-max-values",
    //         "Value must be between 0 and 5",
    //         function (value) {
    //           const number = parseInt(value, 10)
    //           return number >= 0 && number <= 5
    //         }
    //       ),
    //   otherwise: () =>
    //     Yup.string()
    //       .required(getFieldRequiredMessage())
    //       .matches(/^\d+$/, getValidNumMessage())
    //       .test(
    //         "min-max-values",
    //         "Value must be between 0 and 10",
    //         function (value) {
    //           const number = parseInt(value, 10)
    //           return number >= 0 && number <= 10
    //         }
    //       ),
    // }),
    displayOrder: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
  }

  return createSchemaShape(shape)
}

export const addEmailsSchema = () => {
  const shape = {
    backgroundEmails: Yup.string()
      .required(getFieldRequiredMessage())
      .test("multiple-emails", getInvalidFormat(), function (value) {
        if (!value) return false

        const emails = value.split(",").map(email => email.trim()).filter(Boolean)
        if (emails.length === 0) return false

        for (const email of emails) {
          const result = validateEmailAddress(email)
          if (!result.valid) {
            return this.createError({
              message: getEmailValidationErrorMessage(result.code),
            })
          }
        }

        return true
      }),
  }

  return createSchemaShape(shape)
}

export const cloneBranchSchema = () => {
  const shape = {
    branchId: Yup.string().required(getFieldRequiredMessage()),
  }

  return createSchemaShape(shape)
}

export const addVehicleSchema = mode => {
  let shape = {
    brand: englishNameField(),
    brandAr: arabicNameField(),
  }

  if (mode === "add") {
    shape = {
      ...shape,
      image: imageFileField({ required: true }),
    }
  } else {
    shape = {
      ...shape,
      image: imageFileField({ required: false }),
    }
  }

  return createSchemaShape(shape)
}

export const addGiftCardSchema = () => {
  const shape = {
    name: englishNameField(),
    nameAr: arabicNameField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    // startsAt: Yup.string().required(getFieldRequiredMessage()),
    // expiresAt: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .test(
    //     "is-greater",
    //     "Expiry date must be greater than start date",
    //     function (value) {
    //       const { startsAt } = this.parent
    //       return new Date(value) > new Date(startsAt)
    //     }
    //   ),
    amount: Yup.string()
      .required(getFieldRequiredMessage())
      .matches(/^[0-9]+$/, getOnlyNumMessage()),
    // validityInDays: Yup.string()
    //   .required(getFieldRequiredMessage())
    //   .matches(/^[0-9]+$/, getOnlyNumMessage()),
    image: Yup.string().required(getFieldRequiredMessage()),
    cardType: Yup.string().required(getFieldRequiredMessage()),
    customers: Yup.array().when("isLimitToSpecificCustomer", {
      is: val => val === true,
      then: () =>
        Yup.array().test(
          "customers-or-file",
          "Select at least one customer or upload a CSV file with phone numbers",
          function (value) {
            const { file } = this.parent
            const hasCustomers = value && value.length >= 1
            const hasFile = file && file instanceof File
            return hasCustomers || hasFile
          }
        ),
      otherwise: schema => schema.notRequired(),
    }),
  }

  return createSchemaShape(shape)
}

export const addGiftCardCategorySchema = () => {
  return Yup.object().shape({
    name: englishNameField(),
    nameAr: arabicNameField(),
    description: englishDescriptionField(),
    descriptionAr: arabicDescriptionField(),
    cardType: noOnlySpacesOrNumbers.required(getFieldRequiredMessage()),
  })
}

export const addGiftCardTemplateSchema = () => {
  return Yup.object().shape({
    cardTypeId: Yup.string().required(getFieldRequiredMessage()),
    card_title_on_app_en: englishNameField(),
    card_title_on_app_ar: arabicNameField(),
    share_gift_card_title_en: englishOptionalTitleField(),
    share_gift_card_title_ar: arabicOptionalTitleField(),
    share_gift_card_desc_en: englishDescriptionField(),
    share_gift_card_desc_ar: arabicDescriptionField(),
    purchased_gift_card_popup_title_en: englishOptionalTitleField(),
    purchased_gift_card_popup_title_ar: arabicOptionalTitleField(),
    purchased_gift_card_popup_desc_en: englishDescriptionField(),
    purchased_gift_card_popup_desc_ar: arabicDescriptionField(),
    redeem_gift_card_message_en: englishDescriptionField(),
    redeem_gift_card_message_ar: arabicDescriptionField(),
    gift_card_received_appnotify_title_en: englishOptionalTitleField(),
    gift_card_received_appnotify_title_ar: arabicOptionalTitleField(),
    gift_card_received_appnotify_desc_en: englishDescriptionField(),
    gift_card_received_appnotify_desc_ar: arabicDescriptionField(),
    sms_message_for_receiver_en: englishDescriptionField(),
    sms_message_for_receiver_ar: arabicDescriptionField(),
    socialmedia_message_for_receiver_en: englishDescriptionField(),
    socialmedia_message_for_receiver_ar: arabicDescriptionField(),
    confirm_redeem_card_title_en: englishOptionalTitleField(),
    confirm_redeem_card_title_ar: arabicOptionalTitleField(),
    confirm_redeem_card_message_en: englishDescriptionField(),
    confirm_redeem_card_message_ar: arabicDescriptionField(),
    redeem_now_button_en: englishOptionalTitleField(),
    redeem_now_button_ar: arabicOptionalTitleField(),
    redeem_gift_card_title_en: englishOptionalTitleField(),
    redeem_gift_card_title_ar: arabicOptionalTitleField(),
    whatsapp_message_template: Yup.string(),
    add_to_wallet_app_notify_title_en: englishOptionalTitleField(),
    add_to_wallet_app_notify_title_ar: arabicOptionalTitleField(),
    add_to_wallet_app_notify_msg_en: englishDescriptionField(),
    add_to_wallet_app_notify_msg_ar: arabicDescriptionField(),
  })
}

export const funcName = () => {
  const shape = {}

  return createSchemaShape(shape)
}

export const addStaticMaintenanceSchema = () => {
  let shape = {
    code: noOnlySpacesOrNumbers.required(getFieldRequiredMessage()),
    value: Yup.string()
      .required(getFieldRequiredMessage())
      .test("type-specific-validation", function (value) {
        const { type } = this.parent

        if (!value || !type) return true // Let required validation handle empty values

        switch (type) {
          case "number":
            // Check if value is a valid integer
            if (!/^[+-]?\d+$/.test(value) || isNaN(parseInt(value, 10))) {
              return this.createError({
                message: "Please enter a valid integer number",
              })
            }
            return true
          case "fLoat": // Note: there's a typo in the dataTypes constant
            // Check if value is a valid float
            if (!/^[+-]?\d*\.?\d+$/.test(value) || isNaN(parseFloat(value))) {
              return this.createError({
                message: "Please enter a valid decimal number",
              })
            }
            return true
          case "boolean":
            // Check if value is a valid boolean string
            const lowerValue = value.toLowerCase()
            if (
              !["true", "false", "1", "0", "yes", "no"].includes(lowerValue)
            ) {
              return this.createError({
                message: "Please enter: true, false, 1, 0, yes, or no",
              })
            }
            return true
          case "text":
          default:
            // Text type accepts any string
            return true
        }
      }),
    type: noOnlySpacesOrNumbers.required(getFieldRequiredMessage()),
  }
  return createSchemaShape(shape)
}
export const addMenuSetupSchemas = ({ requirePosFields = false } = {}) => {
  const shape = {
    menuId: Yup.string(),
    appMenuId: requirePosFields
      ? Yup.string().trim().required(getFieldRequiredMessage())
      : Yup.string(),
    menuNameEn: englishNameField(),
    menuNameAr: arabicNameField(),
    menuDescriptionEn: englishDescriptionField(),
    menuDescriptionAr: arabicDescriptionField(),
    countryCode: Yup.array(),
  }
  return createSchemaShape(shape)
}

export const brandFormSchema = () => {
  const shape = {
    brandName: brandNameField(),
    appBrandId: appBrandIdField(),
    brandDesc: brandUnsafeTextField(DESCRIPTION_MAX_LENGTH).optional(),
    integration: Yup.string()
      .nullable()
      .oneOf(["Salasa", "Syrve", "Foodics", "", null]),
    status: Yup.number().oneOf([0, 1]),
  }
  return createSchemaShape(shape)
}
